import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import Form from '../../../common/Form';
import Upload from '../../../common/Form/FileUpload';
import { useForm } from 'react-hook-form';
import { actualValueForDropdown } from '../../../utils/common';
import { bulkTicketCreate } from '../../../actions/service_request';
import { postImageUrl } from '../../../actions/users';
import DownloadCSVButton from '../../../utils/CreateCsv';
import Project from '../../../components/Inputs/Project';
import Customer from '../../../components/Inputs/Customer';
import Warehouse from '../../../components/Inputs/Warehouse';
import { canManageOtherOrgs } from '../../../utils/access';

const formName = 'bulk_upload_sites';

const getBulkType = (type) => {
    const supportedBulkTypes = [
        'LOCATION_CREATE',
        'ASSET_CREATE',
        'TICKET_CREATE',
        'LOG_TICKET_OFFLINE_PAYMENTS',
        'SPARE_ASSET_CREATE',
        'EDIT_TICKET_AND_LEDGER_DATES',
        'ASSET_CONFIG_CREATE'
    ];
    return supportedBulkTypes.includes(type) ? type : undefined;
};

// Transform CSV headers to snake_case before upload, otherwise pass the file through unchanged.
const prepareFileForUpload = (file) => new Promise((resolve) => {
    if (!file || file.type !== 'text/csv') {
        resolve(file);
        return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
        const csvContent = event.target.result;
        const lines = csvContent.split('\n');

        if (lines.length === 0) {
            resolve(file);
            return;
        }

        const headers = lines[0].split(',');
        const snakeCaseHeaders = headers.map(header => {
            const cleanHeader = header.trim().replace(/^"|"$/g, '');
            return cleanHeader
                .split(/[\s_-]+/)
                .map(word => word.toLowerCase())
                .join('_');
        });

        const newContent = [
            snakeCaseHeaders.join(','),
            ...lines.slice(1)
        ].join('\n');

        resolve(new File([newContent], file.name, { type: 'text/csv' }));
    };
    reader.onerror = () => resolve(file);
    reader.readAsText(file);
});

export default function BulkUploadModal({ type, site_type }) {
    const dispatch = useDispatch();
    const { formButtonLoading } = useSelector((state) => state.loading);
    const [isUploading, setIsUploading] = useState(false);
    const methods = useForm({ defaultValues: {}, mode: 'all' });
    const selectedOrg = methods.watch('customer');
    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const showCustomerDropdown = canManageOtherOrgs(newLoggedUserData.actions);

    const buildCreatePayload = (formValues, url) => {
        const org = selectedOrg ? selectedOrg?.id : formValues?.project?.org_id;

        const payload = {
            formName,
            type,
            url,
            org_id: org,
            project_id: actualValueForDropdown(formValues.project)
        };

        if (!showCustomerDropdown && (type === 'ASSET_CREATE' || type === 'LOCATION_CREATE' || type === 'SPARE_ASSET_CREATE')) {
            payload.org_id = newLoggedUserData.org.id;
        };

        if (type === 'ASSET_CONFIG_CREATE') {
            payload.org_id = newLoggedUserData.org.id;
        }

        if (site_type) {
            payload.site_type = site_type;

            if (site_type === 'WAREHOUSE') {
                payload.warehouse_id = actualValueForDropdown(formValues.warehouse);
            }
        }

        return payload;
    };

    const submit = async (formValues) => {
        const file = formValues?.bulk_sites?.length > 0 ? formValues.bulk_sites[0] : null;
        if (!file) return;

        setIsUploading(true);
        const fileToUpload = await prepareFileForUpload(file);

        dispatch(postImageUrl({
            files: [fileToUpload],
            ref: 'bulk_sites',
            purpose: 'BULK',
            bulk_type: getBulkType(type),
            onSuccess: (url) => {
                dispatch(bulkTicketCreate(buildCreatePayload(formValues, url)));
                setIsUploading(false);
            },
            onError: () => setIsUploading(false)
        }));
    };

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            formName={formName}
            submitButtonText={'Upload'}
            onCancel={() => dispatch(closeModalAction())}
            showSubmit
            submitContainerClassName='flex gap-2 justify-end py-4 px-4'
            isLoading={isUploading || formButtonLoading[formName]}
        >
            <BulkUploadInputs type={type} site_type={site_type} showCustomerDropdown={showCustomerDropdown} />
        </Form>
    );
}

const BulkUploadInputs = ({ type, site_type, showCustomerDropdown }) => {

    return (
        <div className="flex flex-col w-full gap-2 overflow-hidden">
            <div className="px-4 flex">
                {type === 'TICKET_CREATE' && (
                    <Project
                        className="w-full"
                        name="project"
                        label="Select Project"
                        org_check_required={false}
                    />
                )}

                {showCustomerDropdown && (type === 'ASSET_CREATE' || type === 'LOCATION_CREATE' || type === 'SPARE_ASSET_CREATE') ? (
                    <Customer
                        className="w-full"
                        name="customer"
                        label="Customer"
                        required
                    />
                ) : null}

                {(type === 'SPARE_ASSET_CREATE' && site_type === 'WAREHOUSE') && (
                    <Warehouse
                        className="w-full"
                        name="warehouse"
                        label="Warehouse"
                        required
                    />
                )}

                <Upload
                    className="w-full"
                    name="bulk_sites"
                    label="Upload CSV"
                    required
                />
            </div>

            <div className="px-2">
                <DownloadCSVButton type={type} />
            </div>
        </div>
    );
};